import {
	ComboBox,
	FlexColumn,
	FlexRow,
	IconTooltip,
	Label,
	Modal,
	Separator,
	Text,
	TextField,
	theme,
} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import {noop} from 'lodash';
import {EmailUniqueValidator, GuestUserFormValues, useGuestUserForm} from './hooks/useGuestUserForm';
import {FormFieldSlot, LabelWithTooltipField} from './ClientContactModal.styles';
import ProjectAccessField from './ProjectAccessField';
import type {ProjectOption} from './ClientContactModal';

export type CreateGuestUserModalProps = {
	clientName: string;
	projectOptions: ProjectOption[];
	isProjectsLoading: boolean;
	onClose: () => void;
	onSubmit: (values: GuestUserFormValues) => void;
	isSubmitting?: boolean;
	validateEmailUnique?: EmailUniqueValidator;
	/**
	 * Server-side email error (e.g. "email already in use" surfaced by
	 * `CreatePersonMutation.errors`). Shown alongside the hook's own
	 * client-side `emailError`. Cleared by the container when the user
	 * edits the email field via `onClearServerEmailError`.
	 */
	serverEmailError?: string;
	onClearServerEmailError?: () => void;
};

const CreateGuestUserModal = ({
	clientName,
	projectOptions,
	isProjectsLoading,
	onClose,
	onSubmit,
	isSubmitting = false,
	validateEmailUnique,
	serverEmailError,
	onClearServerEmailError,
}: CreateGuestUserModalProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const {
		firstName,
		handleFirstNameChange,
		firstNameError,
		lastName,
		handleLastNameChange,
		lastNameError,
		email,
		handleEmailChange,
		emailError,
		handleEmailBlur,
		phone,
		handlePhoneChange,
		availableProjects,
		selectedProjects,
		handleAddProject,
		handleRemoveProject,
		handleSubmit,
	} = useGuestUserForm(projectOptions, true, noop, onSubmit, validateEmailUnique);

	// Client ComboBox needs an item matching the disabled value so the label renders.
	const clientItems = [{id: 'current-client', name: clientName}];

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={onClose}>
			<Modal.Title>{formatMessage({id: 'guest_user.create.modal_title'})}</Modal.Title>
			<Modal.Content>
				<div data-cy="create-guest-user-modal">
					<FlexColumn gap={'l'}>
						<LabelWithTooltipField>
							<ComboBox
								aria-label={formatMessage({id: 'client_contact.field.client'})}
								defaultItems={clientItems}
								value={'current-client'}
								disabled
								width={'flex'}
							>
								{item => (
									<ComboBox.Item textValue={item.name}>
										<Text noWrap>{item.name}</Text>
									</ComboBox.Item>
								)}
							</ComboBox>
							<FlexRow gap={'xs'} alignItems={'center'}>
								<Label>{formatMessage({id: 'client_contact.field.client'})}</Label>
								<IconTooltip>
									<IconTooltip.Item
										title={formatMessage({id: 'guest_user.tooltip.client.title'})}
										details={formatMessage({id: 'guest_user.tooltip.client'})}
									/>
								</IconTooltip>
							</FlexRow>
						</LabelWithTooltipField>

						<FlexRow gap={'l'} alignItems="start">
							<FormFieldSlot>
								<TextField
									data-cy="create-guest-user-first-name-input"
									label={formatMessage({id: 'client_contact.field.first_name'})}
									value={firstName}
									onValueChange={handleFirstNameChange}
									hasError={!!firstNameError}
									errorText={firstNameError || undefined}
									size="large"
									width="flex"
								/>
							</FormFieldSlot>
							<FormFieldSlot>
								<TextField
									data-cy="create-guest-user-last-name-input"
									label={formatMessage({id: 'client_contact.field.last_name'})}
									value={lastName}
									onValueChange={handleLastNameChange}
									hasError={!!lastNameError}
									errorText={lastNameError || undefined}
									size="large"
									width="flex"
								/>
							</FormFieldSlot>
						</FlexRow>

						<FormFieldSlot>
							<TextField
								data-cy="create-guest-user-email-input"
								label={formatMessage({id: 'client_contact.field.email'})}
								value={email}
								onValueChange={val => {
									if (serverEmailError) onClearServerEmailError?.();
									handleEmailChange(val);
								}}
								onBlur={handleEmailBlur}
								hasError={!!(serverEmailError || emailError)}
								errorText={serverEmailError || emailError || undefined}
								size="large"
								width="flex"
							/>
						</FormFieldSlot>

						<FormFieldSlot>
							<TextField
								data-cy="create-guest-user-phone-input"
								label={formatMessage({id: 'guest_user.field.phone'})}
								value={phone}
								onValueChange={handlePhoneChange}
								size="large"
								width="flex"
							/>
						</FormFieldSlot>

						<div style={{marginTop: theme.spacing['200']}}>
							<Separator />
						</div>

						<ProjectAccessField
							availableProjects={availableProjects}
							selectedProjects={selectedProjects}
							isLoading={isProjectsLoading}
							onAdd={handleAddProject}
							onRemove={handleRemoveProject}
							labelMessageId={'guest_user.field.project_access'}
							tooltipTitleMessageId={'guest_user.tooltip.project_access.title'}
							tooltipDetailsMessageId={'guest_user.tooltip.project_access'}
						/>
					</FlexColumn>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handleSubmit} disabled={isSubmitting}>
					{formatMessage({id: 'guest_user.create.submit_button'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose} disabled={isSubmitting}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default CreateGuestUserModal;
